import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TalentHappyPathAuthService } from '../shared/service/talent-happy-path-auth.service';
import { TalentHappyPathDataService } from '../shared/service/talent-happy-path-data-service';
import { BaseWorkflowStateService, TransitionRule, WorkflowState } from "../shared/service/base-workflow-state.service";

/**
 * QUICK APPLY WORKFLOW STATE SERVICE
 * ==================================
 *
 * PURPOSE:
 * Manages the multi-step workflow for the Quick Apply process.
 * This service knows all the steps a user goes through when quickly applying
 * to a job and enforces the rules about which steps they can access.
 *
 * THE QUICK APPLY JOURNEY:
 * 1. INVITE - User arrives via an email invitation link
 * 2. START/LOGIN - User enters their email to begin
 * 3. OTP - User verifies their identity with a one-time password
 * 4. PASSWORD - (Optional) User sets a new password if needed
 * 5. WELCOME - User sees a welcome screen after authentication
 * 6. SUBMIT - User submits their application
 *
 * ANALOGY:
 * Think of this like an airport security checkpoint. You can't just jump to the gate
 * (submit) - you must go through check-in (start), show your ID (otp), and possibly
 * verify additional information (password). Each checkpoint validates you can proceed
 * to the next step. This service is the airport security system that enforces those rules.
 *
 * HOW IT WORKS:
 * - Components call navigateToState('submit') instead of router.navigate()
 * - This service checks if the user is allowed to go to 'submit' based on their
 *   authentication status and what steps they've completed
 * - If allowed, it navigates. If not, it blocks the transition and explains why
 *
 * BENEFITS:
 * ✓ All workflow logic in one place (not scattered across components)
 * ✓ Easy to understand the complete flow by reading this file
 * ✓ Easy to add new steps or change rules
 * ✓ Easy to test workflow logic without involving UI components
 * ✓ Components become simpler - they just ask "take me to X" and don't worry about validation
 */

/**
 * The possible states/steps in the Quick Apply workflow
 * Each constant represents a screen the user can be on
 */
export enum QuickApplyStates {
  INVITE = 'invite',       // Initial landing from email invite
  START = 'start',         // Email entry / login page
  LOGIN = 'login',         // Alternative entry point for login
  OTP = 'otp',            // One-time password verification
  PASSWORD = 'password',   // Set/reset password page
  WELCOME = 'welcome',     // Welcome screen after first-time auth
  SUBMIT = 'submit',       // Final application submission
  NEW = 'new',             // General application new talent submit page
  UPDATE = 'update'        // General application existing talent submit page
}

@Injectable({
  providedIn: 'root'  // This makes it a singleton - one instance for the whole app
})
export class QuickApplyWorkflowService extends BaseWorkflowStateService {

  // ============================================================================
  // DEPENDENCY INJECTION
  // ============================================================================
  // We use Angular's inject() function to get services we need
  // This is the modern Angular way (as of Angular 14+) instead of constructor injection

  private authService = inject(TalentHappyPathAuthService);
  private dataService = inject(TalentHappyPathDataService);

  constructor(router: Router) {
    super(router);
  }

  // ============================================================================
  // WORKFLOW DEFINITION - THE STATES
  // ============================================================================
  // This defines every screen/step in the Quick Apply process

  protected getWorkflowStates(): WorkflowState[] {
    return [
      {
        name: QuickApplyStates.INVITE,
        path: 'invite',
        label: 'Invitation',
        metadata: { step: 1, requiresAuth: false }
      },
      {
        name: QuickApplyStates.START,
        path: 'start',
        label: 'Get Started',
        metadata: { step: 1, requiresAuth: false }
      },
      {
        name: QuickApplyStates.LOGIN,
        path: 'login',
        label: 'Login',
        metadata: { step: 1, requiresAuth: false }
      },
      {
        name: QuickApplyStates.OTP,
        path: 'otp',
        label: 'Verify Identity',
        metadata: { step: 2, requiresAuth: false }
      },
      {
        name: QuickApplyStates.PASSWORD,
        path: 'password',
        label: 'Set Password',
        metadata: { step: 3, requiresAuth: false }
      },
      {
        name: QuickApplyStates.WELCOME,
        path: 'welcome',
        label: 'Welcome',
        metadata: { step: 4, requiresAuth: true }
      },
      {
        name: QuickApplyStates.SUBMIT,
        path: 'submit',
        label: 'Submit Application',
        metadata: { step: 5, requiresAuth: true }
      },
      {
        name: QuickApplyStates.NEW,
        path: 'new',
        label: 'New Talent',
        metadata: { step: 6, requiresAuth: false }
      },
      {
        name: QuickApplyStates.UPDATE,
        path: 'update',
        label: 'Update Talent',
        metadata: { step: 7, requiresAuth: false }
      }
    ];
  }

  // ============================================================================
  // WORKFLOW DEFINITION - THE RULES
  // ============================================================================
  // This defines when you can move between states
  // Each rule is like a "if condition A is true, you can go from X to Y"

  protected getTransitionRules(): TransitionRule[] {
    return [
      // ========================================================================
      // RULES FROM START/LOGIN/INVITE
      // ========================================================================
      {
        fromState: QuickApplyStates.START,
        toState: QuickApplyStates.OTP,
        canTransition: (context?: any) => {
          // User can go to OTP if they've entered an email and we have talent data
          return this.hasTalentData();
        },
        blockReason: 'Must enter email before proceeding to verification'
      },
      {
        fromState: QuickApplyStates.INVITE,
        toState: QuickApplyStates.OTP,
        canTransition: (context?: any) => {
          return this.hasTalentData();
        },
        blockReason: 'Must provide email information before verification'
      },
      {
        fromState: QuickApplyStates.LOGIN,
        toState: QuickApplyStates.OTP,
        canTransition: (context?: any) => {
          return this.hasTalentData();
        },
        blockReason: 'Must enter email before proceeding to verification'
      },

      // ========================================================================
      // RULES FROM OTP
      // ========================================================================
      {
        fromState: QuickApplyStates.OTP,
        toState: QuickApplyStates.SUBMIT,
        canTransition: (context?: any) => {
          // User can skip directly to submit if they're authenticated
          // This happens when they successfully verify their OTP
          return this.authService.isAuthenticated();
        },
        blockReason: 'Must verify identity before submitting application'
      },
      {
        fromState: QuickApplyStates.OTP,
        toState: QuickApplyStates.PASSWORD,
        canTransition: () => {
          // User can go to password page from OTP
          // Usually happens when they want to reset their password
          return true;
        }
      },
      {
        fromState: QuickApplyStates.OTP,
        toState: QuickApplyStates.START,
        canTransition: () => {
          // User can always go back to start
          return true;
        }
      },
      {
        fromState: QuickApplyStates.OTP,
        toState: QuickApplyStates.INVITE,
        canTransition: (context?: any) => {
          // User can go back to invite if that's where they came from
          return context?.fromInvite === true;
        }
      },

      {
        fromState: QuickApplyStates.OTP,
        toState: QuickApplyStates.NEW,
        canTransition: () => {
          return true;
        }
      },

      {
        fromState: QuickApplyStates.OTP,
        toState: QuickApplyStates.UPDATE,
        canTransition: () => {
          return true;
        }
      },

      // ========================================================================
      // RULES FROM PASSWORD
      // ========================================================================
      {
        fromState: QuickApplyStates.PASSWORD,
        toState: QuickApplyStates.WELCOME,
        canTransition: () => {
          // After setting password, user typically goes to welcome
          return this.authService.isAuthenticated();
        },
        blockReason: 'Must complete authentication before proceeding'
      },
      {
        fromState: QuickApplyStates.PASSWORD,
        toState: QuickApplyStates.OTP,
        canTransition: () => {
          // User can go back to OTP from password
          return true;
        }
      },

      // ========================================================================
      // RULES FROM WELCOME
      // ========================================================================
      {
        fromState: QuickApplyStates.WELCOME,
        toState: QuickApplyStates.SUBMIT,
        canTransition: () => {
          // After welcome, authenticated users can submit
          return this.authService.isAuthenticated();
        },
        blockReason: 'Must be authenticated to submit application'
      },

      // ========================================================================
      // RULES TO SUBMIT (FROM ANY STATE)
      // ========================================================================
      // Note: We define explicit rules above, but this is a catch-all rule
      // that applies when coming from any state not explicitly defined
      {
        fromState: '*', // Wildcard - matches any state
        toState: QuickApplyStates.SUBMIT,
        canTransition: () => {
          // Users can only access submit if they're authenticated
          return this.authService.isAuthenticated();
        },
        blockReason: 'Must be authenticated to submit application'
      }
    ];
  }

  // ============================================================================
  // INITIAL STATE
  // ============================================================================

  protected getInitialState(): WorkflowState {
    // The workflow starts at the START page by default
    return this.getWorkflowStates().find(s => s.name === QuickApplyStates.START)!;
  }

  // ============================================================================
  // HELPER METHODS - CHECKING CONDITIONS
  // ============================================================================
  // These are reusable condition checks used in transition rules

  /**
   * Check if we have talent data from the backend
   * This means the user has entered their email and we've looked them up
   */
  private hasTalentData(): boolean {
    return this.dataService.talent != null &&
           this.dataService.talent.email != null;
  }

  /**
   * Check if the user has an existing password
   * If yes, they need to use it. If no, they need to create one.
   */
  private hasExistingPassword(): boolean {
    return this.dataService.talent?.hasExistingPassword === true;
  }

  /**
   * Check if the user should be redirected to VMS submittal
   * This is a special case for certain types of applications
   */
  private shouldGoToVmsSubmittal(): boolean {
    return this.dataService.goToVmsSub === true;
  }

  // ============================================================================
  // CONVENIENCE METHODS - COMMON NAVIGATION PATTERNS
  // ============================================================================
  // These methods encapsulate common navigation patterns to make components simpler

  /**
   * Handle navigation after successful OTP verification
   * This encapsulates the business logic:
   * - If user should go to VMS submittal, go there
   * - Otherwise, go to submit page
   *
   * Components just call this instead of having this logic themselves
   */
  public async handleSuccessfulOtpVerification(): Promise<boolean> {
    // Check if we need special VMS flow
    if (this.shouldGoToVmsSubmittal()) {
      // Navigate outside the quick-apply workflow to VMS module
      const postingId = this.dataService.postingId;
      return this.router.navigate(['vms-submittal', postingId]);
    }

    // Normal flow: go to submit page
    return this.navigateToState(
      QuickApplyStates.SUBMIT,
      {},
      { skipLocationChange: true }
    );
  }

  /**
   * Handle back navigation from OTP page
   * Logic depends on whether user came from invite or start page
   */
  public async handleBackFromOtp(fromInvite: boolean): Promise<boolean> {
    if (fromInvite) {
      return this.navigateToState(QuickApplyStates.INVITE);
    }
    return this.navigateToState(QuickApplyStates.START);
  }

  /**
   * Handle redirection when authenticated user tries to access OTP page
   * Authenticated users should go straight to submit
   */
  public handleAuthenticatedUserOnOtp(): void {
    if (this.authService.isAuthenticated()) {
      // No await needed - we just trigger the navigation
      void this.navigateToState(QuickApplyStates.SUBMIT);
    }
  }

  /**
   * Navigate to password reset page from OTP
   */
  public async navigateToPasswordPage(route: ActivatedRoute): Promise<boolean> {
    return this.navigateToState(
      QuickApplyStates.PASSWORD,
      {},
      { relativeTo: route.parent, skipLocationChange: true }
    );
  }

  // ============================================================================
  // OVERRIDE: Enhanced transition checking with wildcard support
  // ============================================================================

  /**
   * Override the parent method to support wildcard (*) rules
   * A wildcard rule means "from any state"
   */
  public override canTransitionTo(fromState: string, toState: string, context?: any): boolean {
    // First try to find an exact rule
    let rule = this.getTransitionRule(fromState, toState);

    // If no exact rule, look for a wildcard rule
    if (!rule) {
      rule = this.getTransitionRules().find(
        r => r.fromState === '*' && r.toState === toState
      );
    }

    // If no rule found at all, allow transition by default
    if (!rule) return true;

    return rule.canTransition(context);
  }
}

/*
 * ============================================================================
 * HOW TO USE THIS SERVICE IN COMPONENTS
 * ============================================================================
 *
 * BEFORE (Old Way - Scattered Logic):
 * -------
 * // In component:
 * submit() {
 *   if (this.authService.isAuthenticated()) {
 *     if (this.dataService.goToVmsSub) {
 *       this.router.navigate(['vms-submittal', this.dataService.postingId]);
 *     } else {
 *       this.router.navigate(['submit'], { relativeTo: this.route.parent });
 *     }
 *   } else {
 *     this.router.navigate(['otp'], { relativeTo: this.route.parent });
 *   }
 * }
 *
 * AFTER (New Way - Centralized Logic):
 * -------
 * // In component:
 * constructor(private workflowService: QuickApplyWorkflowService) {}
 *
 * async submit() {
 *   await this.workflowService.navigateToState('submit');
 *   // That's it! The workflow service handles all the validation and routing
 * }
 *
 * // Or use convenience methods:
 * async afterOtpSuccess() {
 *   await this.workflowService.handleSuccessfulOtpVerification();
 * }
 *
 * ============================================================================
 * EXAMPLE COMPONENT INTEGRATION
 * ============================================================================
 *
 * import { Component, OnInit } from '@angular/core';
 * import { QuickApplyWorkflowService, QuickApplyStates } from './quick-apply-workflow.service';
 *
 * @Component({
 *   selector: 'app-quick-apply-otp',
 *   template: '...'
 * })
 * export class QuickApplyOtpComponent implements OnInit {
 *   // Inject the workflow service
 *   constructor(private workflow: QuickApplyWorkflowService) {}
 *
 *   ngOnInit() {
 *     // Check if user should even be on this page
 *     this.workflow.handleAuthenticatedUserOnOtp();
 *   }
 *
 *   async onOtpVerified() {
 *     // Let the workflow service handle the next step
 *     await this.workflow.handleSuccessfulOtpVerification();
 *   }
 *
 *   async goBack() {
 *     const fromInvite = this.checkIfFromInvite();
 *     await this.workflow.handleBackFromOtp(fromInvite);
 *   }
 * }
 *
 * ============================================================================
 * BENEFITS FOR JUNIOR DEVELOPERS
 * ============================================================================
 *
 * 1. SINGLE SOURCE OF TRUTH
 *    Want to understand the Quick Apply flow? Read this one file.
 *    No more hunting through 6 different components.
 *
 * 2. EASIER TO MODIFY
 *    Need to add a new step? Just add a new state and define its rules here.
 *    Need to change when users can access a page? Just modify the rule.
 *
 * 3. EASIER TO TEST
 *    Can test workflow logic without any UI components.
 *    Just test: "given state X and condition Y, can I go to state Z?"
 *
 * 4. LESS DUPLICATED CODE
 *    Navigation logic is written once, used everywhere.
 *    No more copying the same if-else blocks to multiple components.
 *
 * 5. BETTER ERROR MESSAGES
 *    When a transition fails, you get a clear reason why.
 *    "Cannot transition from otp to submit - must verify identity first"
 *
 * 6. EASIER DEBUGGING
 *    Can see the complete history of states the user visited.
 *    Can check "what states can the user reach from here?"
 *
 * ============================================================================
 */
